import React from "react";
import PropTypes from "prop-types";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid2";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Paper,
  Typography,
  FormControl,
  Stack,
  MenuItem,
  Select,
  InputLabel,
} from "@mui/material";
import Button from "@mui/material/Button";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import {
  signupMember,
  resetSignupState,
} from "../../features/signupMemberSlice";

const SignUpComponent: React.FC = () => {
  const waryLogo = require("../../assets/img/wary-logo.png");

  const labNames: string[] = ["Lab One", "Lab Two", "Lab Three"];
  const branches: string[] = ["Branch One", "Branch Two", "Branch Three"];

  const dispatch = useDispatch<AppDispatch>();

  const handleSignUpSubmit = (values: any) => {
    dispatch(signupMember(values))
      .then((response: any) => {
        dispatch(resetSignupState());
      })
      .catch((error: any) => {
        console.log("Error:", error);
      });
  };

  const { values, errors, handleBlur, handleChange, handleSubmit } = useFormik({
    initialValues: {
      labName: "",
      branch: "",
      officialEmail: "",
      mobileNumber: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      labName: Yup.string().required("Required"),
      branch: Yup.string().required("Required"),
      officialEmail: Yup.string()
        .email("Invalid email address")
        .required("Required"),
      mobileNumber: Yup.string().required("Required"),
      password: Yup.string().required("Required"),
      confirmPassword: Yup.string().required("Required"),
    }),
    onSubmit: (values) => {
      handleSignUpSubmit(values);
    },
  });

  return (
    <Grid container direction={"column"}>
      <Grid
        size={{ xs: "grow", md: 4 }}
        offset={{ md: 4 }}
        style={{ marginTop: "2rem" }}
      >
        <Paper square={false} elevation={3} style={{ padding: "2rem" }}>
          <Grid
            display={"flex"}
            justifyContent={"center"}
            style={{ marginBottom: "2rem" }}
          >
            <img src={waryLogo} alt="WARY" width={200} />
          </Grid>
          <Typography variant="h4">Sign Up</Typography>
          <Typography variant="subtitle1">
            Create an account to get started
          </Typography>
          <form autoComplete="off" onSubmit={handleSubmit} noValidate>
            <Grid size={{ xs: 12, md: 12 }}>
              <FormControl fullWidth sx={{ m: 1 }} variant="standard">
                <InputLabel variant="standard" htmlFor="uncontrolled-native">
                  Lab/Hospital
                </InputLabel>
                <Select
                  labelId="lab-hospital-select-label"
                  id="labHospitalSelect"
                  value={values.labName}
                  label="Lab/Hospital"
                  onBlur={(e) =>
                    handleBlur({
                      target: { name: "labName", value: e.target.value },
                    })
                  }
                  onChange={(e) =>
                    handleChange({
                      target: { name: "labName", value: e.target.value },
                    })
                  }
                  error={!!errors.labName}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {labNames.map((labName, index) => (
                    <MenuItem key={index} value={labName}>
                      {labName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ m: 1 }} variant="standard">
                <InputLabel variant="standard" htmlFor="uncontrolled-native">
                  Branch
                </InputLabel>
                <Select
                  labelId="lab-hospital-branch-select-label"
                  id="labHospitalBranchSelect"
                  value={values.branch}
                  label="Lab/HospitalBranch"
                  onBlur={(e) =>
                    handleBlur({
                      target: { name: "branch", value: e.target.value },
                    })
                  }
                  onChange={(e) =>
                    handleChange({
                      target: { name: "branch", value: e.target.value },
                    })
                  }
                  error={!!errors.branch}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {branches.map((branch, index) => (
                    <MenuItem key={index} value={branch}>
                      {branch}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ m: 1 }} variant="standard">
                <TextField
                  fullWidth
                  id="officialEmail"
                  label="Official Email"
                  variant="standard"
                  value={values.officialEmail}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.officialEmail}
                  helperText={errors.officialEmail}
                />
              </FormControl>
              <FormControl fullWidth sx={{ m: 1 }} variant="standard">
                <TextField
                  fullWidth
                  id="mobileNumber"
                  label="Mobile Number"
                  variant="standard"
                  value={values.mobileNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.mobileNumber}
                  helperText={errors.mobileNumber}
                />
              </FormControl>
              <FormControl fullWidth sx={{ m: 1 }} variant="standard">
                <TextField
                  fullWidth
                  id="password"
                  label="Password"
                  variant="standard"
                  type="password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.password}
                  helperText={errors.password}
                />
              </FormControl>
              <FormControl fullWidth sx={{ m: 1 }} variant="standard">
                <TextField
                  fullWidth
                  id="confirmPassword"
                  label="Confirm Password"
                  variant="standard"
                  type="password"
                  value={values.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                />
              </FormControl>
              <FormControl
                fullWidth
                sx={{ m: 1 }}
                variant="standard"
                style={{ marginTop: "2rem" }}
              >
                <Stack direction="row" spacing={2} justifyContent={"flex-end"}>
                  <Button size="large">
                    Go to Home
                  </Button>
                  <Button variant="contained" size="large" type="submit">
                    Sign Up
                  </Button>
                </Stack>
              </FormControl>
            </Grid>
          </form>
        </Paper>
      </Grid>
    </Grid>
  );
};

SignUpComponent.prototype = {
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  userName: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  password: PropTypes.string.isRequired,
};

export { SignUpComponent };
